import { useState, useEffect } from 'react';
import adminApi from '@/lib/adminApi';
import { toast } from 'sonner';
import { Image, Save, Loader2, Eye, EyeOff, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';


export const NavbarBannerTab = ({ onRefresh }) => {
  const [banner, setBanner] = useState({
    image_url: '',
    link_url: '',
    active: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchBanner();
  }, []);

  const fetchBanner = async () => {
    try {
      const res = await adminApi.get(`/navbar-banner`);
      setBanner(res.data);
    } catch (error) {
      console.error('Error fetching navbar banner:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.put(`/navbar-banner`, banner, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
      });
      toast.success('Banner del Navbar guardado');
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Error al guardar el banner');
      console.error('Error saving navbar banner:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Image className="text-[#E91E8C]" size={24} />
          <h2 className="text-xl font-bold text-black">Banner del Navbar</h2>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#E91E8C] hover:bg-[#E91E8C]/80 text-white"
        >
          {saving ? (
            <>
              <Loader2 className="animate-spin mr-2" size={16} />
              Guardando...
            </>
          ) : (
            <>
              <Save className="mr-2" size={16} />
              Guardar
            </>
          )}
        </Button>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-sm text-blue-700">
          Este banner aparece en el navbar después de "Tienda" (solo en desktop).
          En móvil, aparece al abrir el menú hamburguesa, arriba de "Inicio".
          <br /><br />
          <strong>Tamaño recomendado:</strong> 440×60 píxeles
        </p>
      </div>

      {/* Settings Form */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-6">
        {/* Active Toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-3">
            {banner.active ? (
              <Eye className="text-green-500" size={20} />
            ) : (
              <EyeOff className="text-gray-400" size={20} />
            )}
            <div>
              <p className="font-medium text-gray-900">Banner Activo</p>
              <p className="text-sm text-gray-500">
                {banner.active ? 'El banner está visible en el navbar' : 'El banner está oculto'}
              </p>
            </div>
          </div>
          <Switch
            checked={banner.active}
            onCheckedChange={(checked) => setBanner({ ...banner, active: checked })}
          />
        </div>

        {/* Image URL */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Image size={16} />
            URL de la Imagen
            <span className="text-xs text-gray-400 font-normal ml-2">— Recomendado: 440×60 px</span>
          </label>
          <Input
            type="url"
            value={banner.image_url}
            onChange={(e) => setBanner({ ...banner, image_url: e.target.value })}
            placeholder="https://ejemplo.com/banner-navbar.png"
            className="bg-gray-50 border-gray-200"
          />
          {banner.image_url && (
            <div className="mt-2 p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600 block mb-2">Vista previa:</span>
              <div className="inline-block p-2 bg-black rounded" style={{ border: '2px solid white' }}>
                <img 
                  src={banner.image_url} 
                  alt="Banner navbar preview" 
                  className="h-[60px] w-auto"
                  style={{ maxWidth: '440px' }}
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            </div>
          )}
        </div>

        {/* Link URL */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <ExternalLink size={16} />
            URL de Enlace (opcional)
          </label>
          <Input
            type="url"
            value={banner.link_url}
            onChange={(e) => setBanner({ ...banner, link_url: e.target.value })}
            placeholder="https://ejemplo.com/destino"
            className="bg-gray-50 border-gray-200"
          />
          <p className="text-xs text-gray-500">
            Al hacer clic en el banner, se abrirá este enlace en una nueva pestaña
          </p>
        </div>
      </div>
    </div>
  );
};
